/**
 * Service for managing South African address data and providing intelligent suggestions
 */

import { southAfricanAddresses, saPostalCodePatterns, type SACity, type SAProvince } from '@/data/southAfricanAddresses';
import { normalizeString, normalizeCity, normalizeProvinceState } from './addressUtils';

export interface AddressSuggestion {
  type: 'province' | 'city' | 'suburb';
  value: string;
  confidence: number;
  context?: {
    province?: string;
    city?: string;
  };
}

export class SouthAfricanAddressService {
  private static instance: SouthAfricanAddressService;
  private provinces: Record<string, SAProvince>;
  private cityToProvince: Map<string, string> = new Map();
  private suburbToCity: Map<string, string> = new Map();
  private suburbToProvince: Map<string, string> = new Map();

  constructor() {
    this.provinces = southAfricanAddresses;
    this.buildLookupMaps();
  }

  static getInstance(): SouthAfricanAddressService {
    if (!SouthAfricanAddressService.instance) {
      SouthAfricanAddressService.instance = new SouthAfricanAddressService();
    }
    return SouthAfricanAddressService.instance;
  }

  private buildLookupMaps(): void {
    Object.entries(this.provinces).forEach(([provinceName, province]) => {
      Object.entries(province.cities).forEach(([cityName, city]) => {
        this.cityToProvince.set(cityName.toLowerCase(), provinceName);
        
        city.suburbs.forEach(suburb => {
          this.suburbToCity.set(suburb.toLowerCase(), cityName);
          this.suburbToProvince.set(suburb.toLowerCase(), provinceName);
        });
      });
    });
  }

  /**
   * Get all provinces
   */
  getAllProvinces(): string[] {
    return Object.keys(this.provinces);
  }

  /**
   * Get all cities for a province
   */
  getCitiesForProvince(provinceName: string): string[] {
    const normalizedProvince = normalizeProvinceState(provinceName);
    const province = this.provinces[normalizedProvince];
    return province ? Object.keys(province.cities) : [];
  }

  /**
   * Get all suburbs for a city
   */
  getSuburbsForCity(cityName: string): string[] {
    const normalizedCity = normalizeCity(cityName);
    const provinceName = this.cityToProvince.get(normalizedCity.toLowerCase());
    if (!provinceName) return [];

    const province = this.provinces[provinceName];
    const city = province?.cities[normalizedCity];
    return city?.suburbs || [];
  }

  /**
   * Get province for a city
   */
  getProvinceForCity(cityName: string): string | undefined {
    const normalizedCity = normalizeCity(cityName);
    return this.cityToProvince.get(normalizedCity.toLowerCase());
  }

  /**
   * Get city for a suburb
   */
  getCityForSuburb(suburbName: string): string | undefined {
    const normalizedSuburb = normalizeString(suburbName);
    return this.suburbToCity.get(normalizedSuburb.toLowerCase());
  }

  /**
   * Get province for a suburb
   */
  getProvinceForSuburb(suburbName: string): string | undefined {
    const normalizedSuburb = normalizeString(suburbName);
    return this.suburbToProvince.get(normalizedSuburb.toLowerCase());
  }

  /**
   * Validate postal code for a province
   */
  validatePostalCodeForProvince(postalCode: string, provinceName: string): boolean {
    const pattern = saPostalCodePatterns[provinceName as keyof typeof saPostalCodePatterns];
    return pattern ? pattern.test(postalCode) : true; // Default to valid if no pattern
  }

  /**
   * Get postal code suggestions for a city
   */
  getPostalCodesForCity(cityName: string): string[] {
    const normalizedCity = normalizeCity(cityName);
    const provinceName = this.cityToProvince.get(normalizedCity.toLowerCase());
    if (!provinceName) return [];

    const province = this.provinces[provinceName];
    const city = province?.cities[normalizedCity];
    return city?.postalCodes || [];
  }

  /**
   * Get intelligent suggestions based on partial input
   */
  getSuggestions(input: string, type: 'province' | 'city' | 'suburb'): AddressSuggestion[] {
    const normalized = normalizeString(input).toLowerCase();
    const suggestions: AddressSuggestion[] = [];

    if (type === 'province') {
      Object.keys(this.provinces).forEach(province => {
        if (province.toLowerCase().includes(normalized)) {
          suggestions.push({
            type: 'province',
            value: province,
            confidence: this.calculateConfidence(normalized, province.toLowerCase())
          });
        }
      });
    } else if (type === 'city') {
      this.cityToProvince.forEach((province, city) => {
        if (city.includes(normalized)) {
          suggestions.push({
            type: 'city',
            value: this.denormalizeCity(city),
            confidence: this.calculateConfidence(normalized, city),
            context: { province }
          });
        }
      });
    } else if (type === 'suburb') {
      this.suburbToCity.forEach((city, suburb) => {
        if (suburb.includes(normalized)) {
          const province = this.suburbToProvince.get(suburb);
          suggestions.push({
            type: 'suburb',
            value: this.denormalizeSuburb(suburb),
            confidence: this.calculateConfidence(normalized, suburb),
            context: { city: this.denormalizeCity(city), province }
          });
        }
      });
    }

    return suggestions.sort((a, b) => b.confidence - a.confidence).slice(0, 10);
  }

  /**
   * Get auto-completion suggestions when a field is filled
   */
  getAutoCompletionSuggestions(field: 'suburb' | 'city' | 'province', value: string): {
    city?: string;
    province?: string;
    suburbs?: string[];
    postalCodes?: string[];
  } {
    const suggestions: any = {};

    if (field === 'suburb') {
      const city = this.getCityForSuburb(value);
      const province = this.getProvinceForSuburb(value);
      if (city) suggestions.city = city;
      if (province) suggestions.province = province;
      if (city) suggestions.postalCodes = this.getPostalCodesForCity(city);
    } else if (field === 'city') {
      const province = this.getProvinceForCity(value);
      if (province) suggestions.province = province;
      suggestions.suburbs = this.getSuburbsForCity(value);
      suggestions.postalCodes = this.getPostalCodesForCity(value);
    } else if (field === 'province') {
      suggestions.suburbs = this.getCitiesForProvince(value);
    }

    return suggestions;
  }

  private calculateConfidence(input: string, target: string): number {
    if (target.startsWith(input)) return 0.9;
    if (target.includes(input)) return 0.7;
    
    // Simple fuzzy matching
    const distance = this.levenshteinDistance(input, target);
    return Math.max(0, 1 - (distance / target.length));
  }

  private levenshteinDistance(a: string, b: string): number {
    const matrix = [];

    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[b.length][a.length];
  }

  private denormalizeCity(city: string): string {
    // Convert back to proper case
    return city.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  private denormalizeSuburb(suburb: string): string {
    // Convert back to proper case
    return suburb.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }
}

export const saAddressService = SouthAfricanAddressService.getInstance();